import { useState, type ReactNode } from 'react';
import type { PriceSeries, ReserveSnapshot } from '../../shared/types';
import { fmtCompact, fmtPct, fmtUsd, toNumber, usdValue } from '../lib/format';
import { MARKET_HOURS_NOTE, isUsStockMarketClosed } from '../lib/market-hours';
import { hfText, hfTone } from '../lib/position';
import { IconAlert, IconCheck, IconClock, IconExternal, IconInfo, IconPause } from './Icons';

/** Dark page header band with the page title and its headline figures, as on every page of the reference app. */
export function TopBand({ title, children, back }: { title: ReactNode; children?: ReactNode; back?: ReactNode }) {
  return (
    <section className="topband">
      <div className="topband__inner">
        {back}
        <div className="topband__title">{title}</div>
        {children && <div className="topband__stats">{children}</div>}
      </div>
    </section>
  );
}

export function TopStat({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="topstat">
      <span className="topstat__icon">{icon}</span>
      <div><span className="topstat__label">{label}</span><span className="topstat__value">{children}</span></div>
    </div>
  );
}

/** White content card. With `collapsible` it gets the Hide/Show control of the dashboard lists. */
export function Paper({ title, right, chips, children, collapsible, id }: { title: ReactNode; right?: ReactNode; chips?: ReactNode; children: ReactNode; collapsible?: boolean; id?: string }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="paper" aria-labelledby={id}>
      <header className="paper__head">
        <h2 id={id}>{title}</h2>
        <div className="paper__right">
          {right}
          {collapsible && <button type="button" className="text-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show'} <span aria-hidden="true">{open ? '–' : '+'}</span></button>}
        </div>
      </header>
      {open && chips && <div className="chips-row">{chips}</div>}
      {open && children}
    </section>
  );
}

export const InfoChip = ({ label, children }: { label: string; children: ReactNode }) => (
  <span className="info-chip"><span>{label}</span><strong>{children}</strong></span>
);

export function Alert({ tone = 'info', children, action }: { tone?: 'info' | 'warning' | 'critical' | 'success'; children: ReactNode; action?: ReactNode }) {
  const Icon = tone === 'info' ? IconInfo : tone === 'success' ? IconCheck : IconAlert;
  return <div className={`alert alert--${tone}`} role={tone === 'critical' ? 'alert' : undefined}><Icon size={16} /><div className="alert__text">{children}</div>{action}</div>;
}

export const ExternalLink = ({ href, children, className }: { href: string; children: ReactNode; className?: string }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={className ?? 'ext-link'}>{children} <IconExternal size={12} /></a>
);

/** Token amount with its USD value underneath: the amount is the fact, the dollar figure is context. */
export function Amount({ raw, reserve, digits }: { raw: bigint | string; reserve: Pick<ReserveSnapshot, 'decimals' | 'priceUsd'>; digits?: number }) {
  const v = typeof raw === 'string' ? BigInt(raw) : raw;
  return (
    <span className="amount-cell">
      <span>{fmtCompact(toNumber(v, reserve.decimals), digits ?? 4)}</span>
      <small>{fmtUsd(usdValue(v, reserve.decimals, BigInt(reserve.priceUsd)), { compact: true })}</small>
    </span>
  );
}

export const Apy = ({ value }: { value: number }) => <span className="apy">{value === 0 ? '0' : value < 0.0001 ? '<0.01' : (value * 100).toFixed(2)}<span className="apy__unit">%</span></span>;

export function HealthFactor({ value, size }: { value: number; size?: 'lg' }) {
  return <span className={size === 'lg' ? 'hf hf--lg' : 'hf'} data-tone={Number.isFinite(value) ? hfTone(value) : 'good'}>{hfText(value)}</span>;
}

const LTV0_NOTE = 'New borrowing against this stock is switched off right now, for example while its market is closed. Existing positions keep their liquidation threshold.';

/**
 * Status tags of a reserve. Inside a link or a clickable row the explanation can only be a hover title; with `explain`
 * (where the tags are not nested in another control) a tagged state is a button that reveals it, which also works on
 * touch screens and from the keyboard.
 */
export function ReserveTags({ reserve, explain }: { reserve: ReserveSnapshot; series?: PriceSeries; explain?: boolean }) {
  const [note, setNote] = useState<'ltv0' | 'closed' | null>(null);
  const closed = reserve.role === 'collateral' && isUsStockMarketClosed();
  const ltv0 = reserve.role === 'collateral' && reserve.ltvBps === 0;
  const noteTag = (key: 'ltv0' | 'closed', className: string, text: string, children: ReactNode) => explain
    ? <button type="button" className={`${className} tag--btn`} aria-expanded={note === key} onClick={() => setNote((v) => (v === key ? null : key))}>{children}<IconInfo size={11} /></button>
    : <span className={className} title={text}>{children}</span>;
  return (
    <span className="tags">
      {reserve.priceError && <span className="tag tag--critical">No price</span>}
      {reserve.isPaused && <span className="tag tag--critical"><IconPause size={12} />Paused</span>}
      {reserve.isFrozen && !reserve.isPaused && <span className="tag tag--warning">Frozen</span>}
      {ltv0 && noteTag('ltv0', 'tag tag--warning', LTV0_NOTE, 'LTV 0')}
      {closed && noteTag('closed', 'tag', MARKET_HOURS_NOTE, <><IconClock size={12} />Market closed</>)}
      {explain && note && <span className="tag-note" role="note">{note === 'ltv0' ? LTV0_NOTE : MARKET_HOURS_NOTE}</span>}
    </span>
  );
}

/** A label with an (i) button that reveals its explanation below it: tooltips never show on touch screens. */
export function HintLabel({ label, hint, open, onToggle }: { label: string; hint: string; open: boolean; onToggle(): void }) {
  return (
    <span className="hint-label">{label}
      <button type="button" className="hint-btn" aria-expanded={open} aria-label={`${open ? 'Hide' : 'Show'} explanation: ${label}`} title={hint} onClick={onToggle}><IconInfo size={13} /></button>
    </span>
  );
}

/** Ring showing how much of a cap is used. A meter, not a chart: the numbers beside it carry the values. */
export function CapRing({ used, cap, label }: { used: number; cap: number; label: string }) {
  const pct = cap > 0 ? Math.min(1, used / cap) : 0;
  const r = 34, c = 2 * Math.PI * r;
  return (
    <svg className="cap-ring" width={84} height={84} viewBox="0 0 84 84" role="img" aria-label={`${label}: ${fmtPct(pct)} of the cap used`}>
      <circle cx={42} cy={42} r={r} fill="none" stroke="var(--ring-track)" strokeWidth={5} />
      <circle cx={42} cy={42} r={r} fill="none" stroke="var(--accent)" strokeWidth={5} strokeLinecap="round" strokeDasharray={`${Math.max(pct * c, pct > 0 ? 2 : 0)} ${c}`} transform="rotate(-90 42 42)" />
      <text x={42} y={42} dy="0.35em" textAnchor="middle" className="cap-ring__text">{pct > 0 && pct < 0.0001 ? '<0.01%' : fmtPct(pct, pct < 0.1 ? 2 : 1)}</text>
    </svg>
  );
}

export const NoData = ({ children }: { children: ReactNode }) => <p className="no-data">{children}</p>;
